import { getCanRedeemLuxPlusBenefits, getCanViewLuxPlusBenefits, getIsLuxPlusEnabled, getIsPendingRenewalLuxPlusMember } from 'luxPlus/selectors/featureToggle'
import {
  API_CALL, CLEAR_LUX_PLUS_MEMBER_SUBSCRIPTION,
  TOGGLE_SHOP_AS_MEMBER,
} from './actionConstants'
import {
  FETCH_LUX_PLUS_PRODUCT_SUBSCRIPTION,
  FETCH_LUX_PLUS_MEMBER_SUBSCRIPTION,
  UPDATE_LUX_PLUS_MEMBER_SUBSCRIPTION,
  CREATE_LUX_PLUS_MEMBER_FREE_PREVIEW,
  FETCH_LUX_PLUS_MEMBER_FREE_PREVIEW_ELIGIBILITY,
  FETCH_LUX_PLUS_PREFERRED_PAYMENT_METHOD,
  FETCH_LUX_PLUS_ORDER_REDEEMED_SAVINGS,
  FETCH_LUX_PLUS_ORDER_POTENTIAL_SAVINGS,
  FETCH_LUX_PLUS_ACCUMULATIVE_SAVINGS,
} from './apiActionConstants'
import type { MemberSubscriptionUpdateData } from 'api/luxPlus'
import {
  getMemberSubscription,
  getSubscriptionOffer,
  getMemberSubscriptionPoll,
  updateMemberSubscriptionRenewal,
  createMemberFreePreviewPeriod,
  getMemberFreePreviewEligibility,
  getRedeemedBenefitsByOrderId,
  getPotentialSavings,
} from 'api/luxPlus'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { getSavedCardV2, getStripeOffSessionCardByRegionAndPurposeId } from 'api/payment'
import { appViewMessageHandler } from 'lib/window/webViewUtils'
import type { AppAction } from './types/ActionTypes'
import { getCurrentIntl } from 'i18n/util/currentIntl'

/**
 * Fetch all subscription offers including soft-deleted (expired) ones.
 * This allows renewal members to find legacy price offers while new members
 * can filter for current active offers by price.
 */
export function fetchProductSubscription(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    if (
      !getIsLuxPlusEnabled(state) ||
      state.luxPlus.products.subscription.offers ||
      state.luxPlus.products.subscription.fetching ||
      state.luxPlus.products.subscription.error
    ) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_LUX_PLUS_PRODUCT_SUBSCRIPTION,
      request: () => getSubscriptionOffer(state.geo.currentRegionCode, true),
    })
  }
}

interface FetchMemberSubscriptionParams {
  refetch?: boolean
  poll?: boolean
  validate?: (result: App.MemberSubscriptionItem) => boolean
}

const checkValidMemberSubscriptionDispatch = (state: App.State, memberId?: string): boolean => {
  if (!memberId || state.auth.account.luxPlus.member.subscription.fetching) {
    return false
  }

  return true
}

export function fetchMemberSubscription(params: FetchMemberSubscriptionParams = {}): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const memberId = state.auth.account.memberId
    const accessToken = state.auth.accessToken

    const isValidDispatch = checkValidMemberSubscriptionDispatch(state, memberId)
    if (!isValidDispatch || !memberId) return

    /*
      We want to refetch when a new membership has been bought. We want to prevent fetching
      if service returns an error or if the user account does not have a membership.
     */
    const shouldFetch = !state.auth.account.luxPlus.member.subscription.error && state.auth.account.subscriberTier && !state.auth.account.luxPlus.member.subscription.item && getIsLuxPlusEnabled(state)
    if (params.refetch || shouldFetch) {
      dispatch({
        type: API_CALL,
        api: FETCH_LUX_PLUS_MEMBER_SUBSCRIPTION,
        request: () => {
          if (params.poll) {
            return getMemberSubscriptionPoll(memberId, state.geo.currentRegionCode, accessToken, params?.validate)
          } else {
            return getMemberSubscription({
              customerId: memberId,
              regionCode: state.geo.currentRegionCode,
              accessToken,
            })
          }
        },
      })
    }
  }
}

export function updateMemberSubscription(data: MemberSubscriptionUpdateData): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const memberId = state.auth.account.memberId

    const isValidDispatch = checkValidMemberSubscriptionDispatch(state, memberId)
    if (!isValidDispatch || !memberId) return

    dispatch({
      type: API_CALL,
      api: UPDATE_LUX_PLUS_MEMBER_SUBSCRIPTION,
      request: () => updateMemberSubscriptionRenewal(memberId, data),
    })
  }
}

export function clearMemberSubscription(): AppAction {
  return {
    type: CLEAR_LUX_PLUS_MEMBER_SUBSCRIPTION,
  }
}

export function fetchMemberFreePreviewEligibility(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const memberId = state.auth.account.memberId
    const freePreview = state.luxPlus.products.freePreview

    if (
      !memberId ||
      !getIsLuxPlusEnabled(state) ||
      freePreview.fetching ||
      freePreview.error ||
      freePreview.offer
    ) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_LUX_PLUS_MEMBER_FREE_PREVIEW_ELIGIBILITY,
      request: () => getMemberFreePreviewEligibility(memberId),
    })
  }
}

export function createFreePreviewMember(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const memberId = state.auth.account.memberId
    const accessToken = state.auth.accessToken
    const subscription = state.auth.account.luxPlus.member.subscription
    const firstName = state.auth.account.givenName
    const headlessMode = state.system.headlessMode
    let joined = false

    if (
      !memberId ||
      !getIsLuxPlusEnabled(state) ||
      subscription.fetching ||
      subscription.error ||
      getCanViewLuxPlusBenefits(state)
    ) return

    dispatch({
      type: API_CALL,
      api: CREATE_LUX_PLUS_MEMBER_FREE_PREVIEW,
      request: () => createMemberFreePreviewPeriod({
        customerId: memberId,
        region: state.geo.currentRegionCode,
      }, accessToken)
        .then((result): App.MemberSubscriptionItem => {
          joined = true
          const intl = getCurrentIntl()
          const freePreviewMessage = firstName
            ? intl.formatMessage({
                id: 'ojLfp2xge0ohPMdFJT7o',
                defaultMessage: '{firstName}, your {duration}-day free preview for {programName} has started. Enjoy!',
                description: 'Snackbar message when LuxPlus free preview starts with user name',
              }, { firstName, duration: LUXURY_PLUS.FREE_PREVIEW_DURATION, programName: LUXURY_PLUS.PROGRAM_NAME })
            : intl.formatMessage({
                id: 'JWgdxcuZqt-WYHRe6aPe',
                defaultMessage: 'Your {duration}-day free preview for {programName} has started. Enjoy!',
                description: 'Snackbar message when LuxPlus free preview starts',
              }, { duration: LUXURY_PLUS.FREE_PREVIEW_DURATION, programName: LUXURY_PLUS.PROGRAM_NAME })
          showSnackbar(freePreviewMessage, 'success')
          return {
            ...result,
            trigger: 'manual-free-preview-enroll',
          }
        })
        .catch((err) => {
          const intl = getCurrentIntl()
          showSnackbar(
            intl.formatMessage({
              id: 'DIDsDrujBNVbqruv3CLZ',
              defaultMessage: 'Please try again later or contact us for assistance.',
              description: 'Snackbar message when LuxPlus free preview fails',
            }),
            'critical',
            {
              heading: intl.formatMessage({
                id: 'Ax5l5hiW5L75nKlAa9fq',
                defaultMessage: 'Oops! Something went wrong',
                description: 'Snackbar heading when LuxPlus free preview fails',
              }),
            },
          )

          return err
        })
        .finally(() => {
          if (headlessMode) {
            appViewMessageHandler('luxPlusFreePreview', { joined })
          }
        }),
    })
  }
}

export function fetchLuxPlusPreferredPaymentCard(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const region = state.geo.currentRegionCode
    const currency = state.geo.currentCurrency
    const luxPlusMemberSubscriptionId = state.auth.account.luxPlus.member.subscription.item?.id
    const canRedeemLuxPlusBenefits = getCanRedeemLuxPlusBenefits(state)
    const pendingRenewalSubscribedLuxPlusMember = getIsPendingRenewalLuxPlusMember(state)

    if (!luxPlusMemberSubscriptionId || (!canRedeemLuxPlusBenefits && !pendingRenewalSubscribedLuxPlusMember)) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_LUX_PLUS_PREFERRED_PAYMENT_METHOD,
      request: () => getStripeOffSessionCardByRegionAndPurposeId(region, luxPlusMemberSubscriptionId)
        .then(({ paymentMethod }) => {
          return getSavedCardV2(paymentMethod, currency, region)
        }),
    })
  }
}

export function toggleDisableMembership(): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const disableMembership = state.auth.account.luxPlus.member.disableMembership
    dispatch({
      type: TOGGLE_SHOP_AS_MEMBER,
      disableMembership: !disableMembership,
    })
  }
}

export function fetchAccumulativePotentialSavings(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const customerId = state.auth.account.memberId
    const region = state.geo.currentRegionCode
    const isAlreadyMember = getCanRedeemLuxPlusBenefits(state)

    if (
      !customerId ||
      isAlreadyMember ||
      state.luxPlus.accumulativeSavings.fetching ||
      state.luxPlus.accumulativeSavings.data
    ) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_LUX_PLUS_ACCUMULATIVE_SAVINGS,
      request: () => getPotentialSavings({ customerId, region, daysPeriod: '365' }),
    })
  }
}

/**
 * Fetches LuxPlus order savings for a given order.
 * First tries to fetch redeemed benefits (for members), then falls back to potential savings (for non-members).
 */
export function fetchLuxPlusOrderSavings(orderId: string, region: string): AppAction {
  return async(dispatch, getState) => {
    const state = getState()

    // hide banner if luxplus not supported in current region
    if (!getIsLuxPlusEnabled(state) || !orderId) {
      return
    }

    // Check if already fetching or fetched
    const existingSavings = state.luxPlus.orderSavings[orderId]
    if (existingSavings?.fetching || existingSavings?.data) {
      return
    }

    // First, try redeemed benefits (indicates they were a member at booking)
    try {
      const redeemedData = await getRedeemedBenefitsByOrderId(orderId)

      // Only use redeemed savings if there are actual savings
      if (redeemedData.totalSavings > 0) {
        dispatch({
          type: API_CALL,
          api: FETCH_LUX_PLUS_ORDER_REDEEMED_SAVINGS,
          orderId,
          request: () => Promise.resolve(redeemedData),
        })
        return
      }
    } catch {
      // Expected to fail for non-members - fall through to potential savings
    }

    // Try potential savings for non-members
    const customerId = getState().auth.account.memberId
    if (!customerId) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_LUX_PLUS_ORDER_POTENTIAL_SAVINGS,
      orderId,
      request: () => getPotentialSavings({ customerId, region, orderId }),
    })
  }
}
